import React, { useCallback, useRef, useEffect, useState } from "react";
import { Box, Chip, FormControl, Select, Option, Input } from "@mui/joy";
import { useTranslation } from 'react-i18next';
import { Button } from 'src/mtska-frontend-app-component';

const Rules = ({ model, defaultRules = [], onRulesChange, setValue }) => {
	const { t } = useTranslation();
	const [rules, setRules] = useState(() =>
		defaultRules.length > 0
			? defaultRules.map(rule => ({
				field: rule.field || "",
				operator: rule.operator || "=",
				value: rule.value || ""
			}))
			: [{ field: "", operator: "=", value: "" }]
	);
	const isFirstRender = useRef(true);
	const [mounted, setMounted] = useState(false);
	const debounceTimeout = useRef(null);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
		debounceTimeout.current = setTimeout(() => {
			onRulesChange?.(rules);
		}, 500);
		return () => clearTimeout(debounceTimeout.current);
	}, [rules, onRulesChange]);

	const callbackAdd = useCallback(() => {
		const newRules = [
			...rules,
			{ field: "", operator: "=", value: "" }
		];
		setRules(newRules);
	}, [rules]);

	const callbackDelete = useCallback((index) => {
		const newRules = rules.filter((_, i) => i !== index);
		setRules(newRules.length ? newRules : [{ field: "", operator: "=", value: "" }]);
	}, [rules]);

	const callbackUpdate = useCallback((index, key, value) => {
		const newRules = [...rules];
		newRules[index] = { ...newRules[index], [key]: value };
		setRules(newRules);
		setValue?.(`rules[${index}][${key}]`, value);
	}, [rules, setValue]);

	const operators = [
		{ value: 'contains', label: t('contains') },
		{ value: 'doesNotContain', label: t('doesNotContain') },
		{ value: 'greaterThanHas', label: t('greaterThanHas') },
		{ value: 'lessThanHas', label: t('lessThanHas') },
		{ value: 'beforeOrEqual', label: t('beforeOrEqual') },
		{ value: 'afterOrEqual', label: t('afterOrEqual') },
		{ value: 'equals', label: t('equals') },
		{ value: 'startsWith', label: t('startsWith') }
	];

	const exclude = [
		'slug', 'id', 'tenant',
		'audienceBlackList.id', 'audienceBlackList.slug', 'audienceBlackList.tenant', 'audienceBlackList.externalID',
		'rpoLead.id', 'rpoLead.slug', 'rpoLead.tenant', 'rpoLead.externalID',
		'delivery.id', 'delivery.slug', 'delivery.tenant'
	]

	const fieldOptions = model
		.filter(o => !exclude.includes(o.key))
		.map(o => ({
			value: o.key,
			label: o.label
		}))

	if (!mounted) return null

	return (
		<Box className="rules-wrapper">
			<Box sx={{ display: 'grid', gap: '20px' }}>
				{rules.map((rule, index) => {
					const selectedFieldLabel = fieldOptions.find(option => option.value === rule.field)?.label;
					const isLastRow = index === rules.length - 1;
					const showDeleteButton = rules.length > 1;
					const isComplete = rule.field && rule.operator && rule.value;

					return (
						<React.Fragment key={`rule-${index}`}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
								<Box sx={{ flex: 1.5 }}>
									<FormControl>
										<Select
											name={`rules[${index}][field]`}
											onChange={(event, newValue) => callbackUpdate(index, 'field', newValue)}
											value={rule.field || ''}
											renderValue={() => (
												<Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
													{selectedFieldLabel && (
														<Chip size="sm" variant="soft" color="primary">
															{selectedFieldLabel}
														</Chip>
													)}
												</Box>
											)}
											sx={{ display: 'flex', alignItems: 'center', pl: 1, height: '40px' }}
										>
											{fieldOptions.map(option => (
												<Option key={option.value} value={option.value}>
													{option.label}
												</Option>
											))}
										</Select>
									</FormControl>
								</Box>

								<Box sx={{ flex: 0.5 }}>
									<FormControl>
										<Select
											name={`rules[${index}][operator]`}
											onChange={(event, newValue) => callbackUpdate(index, 'operator', newValue)}
											value={rule.operator || ''}
											sx={{ display: 'flex', alignItems: 'center', pl: 1, height: '40px' }}
										>
											{operators.map(option => (
												<Option key={option.value} value={option.value}>
													{option.label}
												</Option>
											))}
										</Select>
									</FormControl>
								</Box>

								<Box sx={{ flex: 1.5 }}>
									<FormControl>
										<Input
											name={`rules[${index}][value]`}
											value={rule.value}
											onChange={(event) => callbackUpdate(index, 'value', event.target.value)}
											placeholder={t('writeTheValue')}
											sx={{ minHeight: '40px' }}
										/>
									</FormControl>
								</Box>

								<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', height: 40 }}>
									<Box sx={{ width: '100px', textAlign: 'center' }}>
										{!isLastRow && (
											<span style={{ color: '#ccc', fontSize: '14px', fontWeight: 'bold', lineHeight: '40px' }}>AND</span>
										)}
										{isLastRow && (
											<Button
												icon='faPlus'
												onClick={callbackAdd}
												sx={{ height: '40px' }}
												color={isComplete ? 'primary' : 'neutral'}
												disabled={!isComplete}
											>
												ADD
											</Button>
										)}
									</Box>
									{showDeleteButton && (
										<Button icon='faTrash' onClick={() => callbackDelete(index)} sx={{ height: '40px' }} />
									)}
								</Box>
							</Box>
						</React.Fragment>
					);
				})}
			</Box>
		</Box>
	);
};

export default Rules;